import React, { useState, useEffect, useRef } from 'react';
import LightboxModal from './LightboxModal';
import { getOptimizedImageUrl } from '../lib/imageOptimizer';

export default function CmsGallery({ slug, title = 'Galleri & udvalgte løsninger', description = '' }) {
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  const sliderRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const fetchGallery = async () => {
      try {
        setLoading(true);
        // Try fetching via CMS v2 endpoint with filter
        const res = await fetch(`https://api.dev.auxo.dk/public/v2/content/galleri?gallery_slug=${encodeURIComponent(slug)}`);
        if (res.ok) {
          const json = await res.json();
          if (json.items && json.items.length > 0) {
            if (isMounted) {
              setGalleryData(json.items[0]);
              setLoading(false);
              return;
            }
          }
        }

        // Fallback: list all galleri items
        const fallbackRes = await fetch(`https://api.dev.auxo.dk/public/content/galleri`);
        if (fallbackRes.ok) {
          const list = await fallbackRes.json();
          const match = Array.isArray(list) ? list.find(item => item.gallery_slug === slug || (item.slug && item.slug._ === slug)) : null;
          if (match && isMounted) {
            setGalleryData(match);
          }
        }
      } catch (err) {
        console.warn('Could not load CMS gallery, using fallback if available', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (slug) {
      fetchGallery();
    }
    return () => {
      isMounted = false;
    };
  }, [slug]);

  const images = galleryData?.images || [];

  const updateScrollState = () => {
    const el = sliderRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);

    // Calculate approximate active slide index
    const cardWidth = el.firstElementChild?.clientWidth || clientWidth;
    const currentIdx = Math.round(scrollLeft / cardWidth);
    setActiveSlide(Math.min(Math.max(0, currentIdx), images.length - 1));
  };

  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [images]);

  const scroll = (direction) => {
    const el = sliderRef.current;
    if (!el) return;

    const cardWidth = el.firstElementChild?.clientWidth || 320;
    const gap = 24; // 1.5rem (gap-6)
    const amount = (cardWidth + gap) * (direction === 'next' ? 1 : -1);

    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const scrollToSlide = (index) => {
    const el = sliderRef.current;
    if (!el || !el.children[index]) return;

    const targetChild = el.children[index];
    el.scrollTo({
      left: targetChild.offsetLeft - el.offsetLeft,
      behavior: 'smooth'
    });
  };

  const handleOpenLightbox = (index) => {
    setSelectedIdx(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="flex gap-6 animate-pulse overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div key={i} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] shrink-0 aspect-[4/3] bg-neutral-200 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className="w-full relative">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#FAF6ED] border border-[#EAD5A5] text-[#8D6D2F] text-xs font-semibold uppercase tracking-wider shadow-xs mb-2">
            <i className="fa-solid fa-camera-viewfinder" aria-hidden="true" />
            Dokumenteret arbejde
          </div>
          <h3 className="mt-2.5 pt-1 text-2xl sm:text-3xl font-bold tracking-tight text-neutral-900">
            {galleryData?.title || title}
          </h3>
          {(galleryData?.description || description) && (
            <p className="mt-2 text-neutral-600 max-w-2xl text-base">
              {galleryData?.description || description}
            </p>
          )}
        </div>

        {/* Slider Controls */}
        {images.length > 1 && (
          <div className="flex items-center gap-3 self-start sm:self-auto shrink-0">
            <div className="text-xs font-medium text-neutral-500 mr-2 bg-neutral-100 px-3 py-1.5 rounded-full border border-neutral-200/80">
              <span className="font-semibold text-neutral-900">{activeSlide + 1}</span> / {images.length}
            </div>

            <button
              type="button"
              onClick={() => scroll('prev')}
              disabled={!canScrollLeft}
              className={`w-11 h-11 rounded-xl flex items-center justify-center border transition-all duration-200 ${
                canScrollLeft
                  ? 'bg-white border-neutral-300 text-neutral-900 shadow-sm hover:bg-[#FAF6ED] hover:border-[#8D6D2F] hover:text-[#8D6D2F] cursor-pointer active:scale-95'
                  : 'bg-neutral-100 border-neutral-200 text-neutral-400 cursor-not-allowed opacity-60'
              }`}
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left text-sm" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={() => scroll('next')}
              disabled={!canScrollRight}
              className={`w-11 h-11 rounded-xl flex items-center justify-center border transition-all duration-200 ${
                canScrollRight
                  ? 'bg-white border-neutral-300 text-neutral-900 shadow-sm hover:bg-[#FAF6ED] hover:border-[#8D6D2F] hover:text-[#8D6D2F] cursor-pointer active:scale-95'
                  : 'bg-neutral-100 border-neutral-200 text-neutral-400 cursor-not-allowed opacity-60'
              }`}
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right text-sm" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      {/* Slider Carousel Container */}
      <div className="relative group/carousel">
        <div
          ref={sliderRef}
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 pt-1 px-1 -mx-1 select-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {images.map((imgUrl, idx) => (
            <div
              key={idx}
              onClick={() => handleOpenLightbox(idx)}
              className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] shrink-0 snap-start group relative overflow-hidden rounded-2xl bg-neutral-900 border border-neutral-200/90 shadow-sm hover:shadow-xl hover:border-[#C5A059] cursor-pointer transition-all duration-300 aspect-[4/3]"
            >
              <img
                src={getOptimizedImageUrl(imgUrl, { width: 900, height: 675, quality: 85 })}
                alt={`${galleryData?.title || 'Galleri'} - Billede ${idx + 1}`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
                draggable="false"
              />

              {/* Number Badge */}
              <div className="absolute top-3.5 left-3.5 px-2.5 py-1 rounded-lg bg-neutral-950/70 backdrop-blur-md border border-white/10 text-white text-[11px] font-semibold tracking-wider">
                {String(idx + 1).padStart(2, '0')}
              </div>

              {/* Overlay with zoom icon */}
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/70 via-neutral-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="w-13 h-13 rounded-2xl bg-[#8D6D2F]/90 backdrop-blur-md border border-[#DFBF77] text-white flex items-center justify-center shadow-xl transform scale-90 group-hover:scale-100 transition-all duration-300">
                  <i className="fa-solid fa-magnifying-glass-plus text-lg" aria-hidden="true" />
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-center text-xs text-white/90 font-medium">
                  Klik for at se i fuld skærm
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Floating Side Arrows (Desktop) */}
        {images.length > 3 && (
          <>
            {canScrollLeft && (
              <button
                type="button"
                onClick={() => scroll('prev')}
                className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 rounded-full bg-white/95 backdrop-blur-md border border-neutral-200 text-neutral-800 shadow-xl items-center justify-center hover:bg-[#8D6D2F] hover:text-white hover:border-[#8D6D2F] transition-all duration-200 z-10 cursor-pointer"
                aria-label="Forrige slide"
              >
                <i className="fa-solid fa-chevron-left text-sm" aria-hidden="true" />
              </button>
            )}
            {canScrollRight && (
              <button
                type="button"
                onClick={() => scroll('next')}
                className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 rounded-full bg-white/95 backdrop-blur-md border border-neutral-200 text-neutral-800 shadow-xl items-center justify-center hover:bg-[#8D6D2F] hover:text-white hover:border-[#8D6D2F] transition-all duration-200 z-10 cursor-pointer"
                aria-label="Næste slide"
              >
                <i className="fa-solid fa-chevron-right text-sm" aria-hidden="true" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Dots Indicator */}
      {images.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          {images.map((_, dotIdx) => (
            <button
              key={dotIdx}
              type="button"
              onClick={() => scrollToSlide(dotIdx)}
              className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                activeSlide === dotIdx
                  ? 'w-8 bg-[#8D6D2F]'
                  : 'w-2.5 bg-neutral-300 hover:bg-neutral-400'
              }`}
              aria-label={`Gå til billede ${dotIdx + 1}`}
            />
          ))}
        </div>
      )}

      {/* Lightbox Modal */}
      <LightboxModal
        images={images}
        initialIndex={selectedIdx || 0}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
}
